import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Clear, Check, ErrorOutline } from '@mui/icons-material';
import { Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import { BinType } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import { PickingInfo, PickingListBySKUs } from 'commons/types';
import {
  getProductDetail,
  postPickBasketVerification,
  postPickedItem,
  postRemoveBatch,
} from 'redux-stores/actions';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { IPickingJobPickedItemVerificationRequest } from 'services/apis/SwipeRxWmsApiPickJob/types';

import { IPickProcessState } from '../interfaces';
import S from '../PickItemPage.style';

interface IPickingProcessFormProps {
  pickList: PickingListBySKUs;
  state: IPickProcessState;
  stateKey: string; // to point which state from pickProcessState
  skuLowStockTriggered: { [K: string]: boolean };
  setIsAllBasketVerified: (isAllBasketVerified: boolean) => void;
  pickProcessState: Map<string, IPickProcessState>;
  setPickProcessState: (state: Map<string, IPickProcessState>) => void;
  // barcodeVerified: boolean;
  maxQuantity?: number;
}

const PickingProcessForm: React.FC<IPickingProcessFormProps> = ({
  pickList,
  state,
  stateKey: key,
  skuLowStockTriggered,
  setIsAllBasketVerified,
  pickProcessState,
  setPickProcessState,
  maxQuantity = Number.POSITIVE_INFINITY,
  // barcodeVerified,
}) => {
  const dispatch = useDispatch();
  const recommendationTotalQty = pickList.pick_recommendation.reduce(
    (total, rec) => total + rec.quantity,
    0,
  );
  const isStockOut =
    !skuLowStockTriggered[pickList.sku_code] &&
    recommendationTotalQty < pickList.total_quantity;

  // debounce functions
  const debouncedRemoveBatch = useDebouncedCallback(
    (params: {
      binNumber: string;
      inventoryNumber: string;
      poNumber: string;
      skuCode: string;
      basket: number;
    }) => {
      postRemoveBatch(params);
    },
    500,
    {
      maxWait: 2000,
    },
  );
  const debouncedProductDetail = useDebouncedCallback(
    async (params: {
      binNumber: string;
      skuCode: string;
      inventoryNumber: string;
      _state: IPickProcessState;
      pickIndex: number;
      _key: string;
    }) => {
      const { _state, pickIndex, _key, ...restParams } = params;
      const result = await getProductDetail(restParams, dispatch);
      const clonedPicked = structuredClone(state.picked);
      if (result) {
        if (result.bin_type !== BinType.RETAIL) {
          clonedPicked[pickIndex].bin = '';
          clonedPicked[pickIndex].inventory_number = '';
          dispatch(
            snackbarSetData({
              open: true,
              message: 'Only Retail Bin Allowed!',
              color: colors.PRIMARY_RED,
            }),
          );
        } else {
          clonedPicked[pickIndex].expiry_date = result.expiry_date.toString();
          clonedPicked[pickIndex].bin = result.bin;
          clonedPicked[pickIndex].inventory_number = result.inventory_number;
        }
      } else {
        clonedPicked[pickIndex].bin = '';
        clonedPicked[pickIndex].inventory_number = '';
      }

      setPickProcessState(
        new Map(pickProcessState.set(key, { ...state, picked: clonedPicked })),
      );
    },
    500,
    { maxWait: 2000, trailing: true },
  );
  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);
  const debouncedPickItemVerification = useDebouncedCallback(
    async (
      verificationParams: IPickingJobPickedItemVerificationRequest,
      params: {
        _state: IPickProcessState;
        pickIndex: number;
        _key: string;
      },
    ): Promise<void> => {
      const { _state, pickIndex, _key } = params;
      const result = await postPickBasketVerification(
        verificationParams,
        dispatch,
      );
      const clonedPicked = structuredClone(state.picked);
      clonedPicked[pickIndex].is_verified = !!result;
      clonedPicked[pickIndex].basket_verification = undefined;
      setPickProcessState(
        new Map(
          pickProcessState.set(_key, { ..._state, picked: clonedPicked }),
        ),
      );
    },
    1000,
    { maxWait: 2000 },
  );
  const debouncedPickItem = useDebouncedCallback(
    (params: {
      binNumber: string;
      basket: number;
      expiryDate: Date;
      inventoryNumber: string;
      quantity: number;
      poNumber: string;
      skuCode: string;
    }) => {
      postPickedItem(params, dispatch);
    },
    500,
    {
      maxWait: 2000,
    },
  );

  // states
  useEffect(() => {
    const initPickProcessState = (): Map<string, IPickProcessState> => {
      const result = new Map();
      const defaultPickingInfo = (
        basket: number,
        poNumber: string,
      ): IPickProcessState => ({
        poNumber,
        isOpen: true,
        picked: [
          {
            basket,
            bin: '',
            inventory_number: '',
            expiry_date: '',
            quantity: 0,
            is_verified: false,
          },
        ],
      });

      pickList?.details.forEach((detail) =>
        detail.baskets.forEach((basket) => {
          const _sku = pickList.sku_code;
          const _key = `${detail.po_number}-${_sku}-${basket}`;
          // check existing data. if no data picked yet, use defaultPickingInfo
          const existingPickedData = detail.picked_actual.filter(
            (picked) => picked.basket === basket,
          );
          if (existingPickedData.length > 0) {
            result.set(_key, {
              isOpen: true,
              picked: existingPickedData,
              quantity: detail.quantity,
              poNumber: detail.po_number,
            });
          } else {
            result.set(_key, defaultPickingInfo(basket, detail.po_number));
          }
        }),
      );
      return result;
    };
    setPickProcessState(initPickProcessState());
  }, [pickList?.details, pickList?.sku_code, setPickProcessState]);

  return state.picked.map((info, idx) => {
    // reduce max quantity with other picked qty to get more precise result
    const _maxQuantity =
      maxQuantity -
      state.picked.reduce(
        (sum, _info, _idx) => (idx === _idx ? sum : sum + _info.quantity),
        0,
      );
    const replaceBatch = (
      index: number,
      pickingInfoList: PickingInfo[],
      newPickingInfo: PickingInfo,
    ): PickingInfo[] => {
      if (index < 0 && index >= pickingInfoList.length) {
        return state.picked;
      }
      const updatedPicked = structuredClone(pickingInfoList);
      const replaced = updatedPicked.splice(index, 1, newPickingInfo);
      if (replaced) {
        const { poNumber } = state;
        replaced.forEach((item) => {
          const {
            bin: binNumber,
            inventory_number: inventoryNumber,
            basket,
          } = item;
          debouncedRemoveBatch({
            binNumber,
            inventoryNumber,
            poNumber,
            skuCode: pickList.sku_code,
            basket,
          });
        });
      }

      return updatedPicked;
    };

    const onFieldChange = (
      type: keyof PickingInfo,
      value: string,
      nextField?: string,
    ): void => {
      let clonedPicked = structuredClone(state.picked);
      const prevValue = clonedPicked[idx][type];
      const cleanNewValue = value.trim();

      if (
        (type === 'bin' || type === 'inventory_number') &&
        clonedPicked[idx].quantity !== 0
      ) {
        const newBatch: PickingInfo = {
          bin: '',
          inventory_number: '',
          expiry_date: '',
          quantity: 0,
          basket: clonedPicked[idx].basket,
          is_verified: false,
          basket_verification: 0,
        };
        newBatch[type] = cleanNewValue;
        if (newBatch.bin !== '' && newBatch.inventory_number !== '') {
          clonedPicked.splice(idx, 1, newBatch);
        } else {
          clonedPicked = replaceBatch(idx, clonedPicked, newBatch);
        }
      } else if (
        /*
        data type number
        need to be in this chained OR format, so the else block could executed properly 
        */
        type === 'quantity' ||
        type === 'basket' ||
        type === 'basket_verification'
      ) {
        // condition for tally scanning; read barcode to match the value with registered value in data
        // first, we have to make sure value comes is from "scanned" numbers
        // we assume that "hopping" digit length input from previous one will be treated as barcode scanning
        // example; prev value 4, and suddenly new digits is 401234; it will be treated as barcode scanning
        // const isBarcodeScanning =
        //   cleanNewValue.length - clonedPicked[idx].quantity.toString().length >
        //   4;

        // if (
        //   isBarcodeScanning &&
        //   pickList.barcode &&
        //   cleanNewValue.includes(pickList.barcode)
        // ) {
        //   if (clonedPicked[idx].quantity + 1 <= maxQuantity)
        //     clonedPicked[idx].quantity += 1;
        // } else if (!Number.isNaN(+cleanNewValue) && !isBarcodeScanning) {
        if (!Number.isNaN(+cleanNewValue)) {
          if (type === 'quantity' && cleanNewValue !== '') {
            const fieldQuantity =
              cleanNewValue && +cleanNewValue > 0
                ? Math.floor(+cleanNewValue)
                : 0;
            clonedPicked[idx].quantity =
              fieldQuantity > _maxQuantity ? _maxQuantity : fieldQuantity;
            clonedPicked[idx].basket_verification = 0;
          } else if (type === 'basket_verification') {
            if (+cleanNewValue <= 0) {
              /* ignore invalid value. skip */
              return;
            }
            clonedPicked[idx].basket_verification = +cleanNewValue;
          }
        }
      } else if (type === 'is_verified') {
        // should not be inputted by user, ignore
        return;
      } else {
        clonedPicked[idx][type] = cleanNewValue;
        clonedPicked[idx].basket_verification = 0;
      }
      clonedPicked[idx].is_verified = false;

      const newPickProcessState = structuredClone(pickProcessState);
      newPickProcessState.set(key, { ...state, picked: clonedPicked });
      setPickProcessState(newPickProcessState);

      /* any edit, all basket must be re-verified */
      setIsAllBasketVerified(false);

      /* debounce after state assignment to prevent race condition */
      const {
        bin,
        expiry_date: expiryDate,
        inventory_number: inventoryNumber,
        quantity,
        basket,
        basket_verification: basketVerification,
      } = clonedPicked[idx];
      if (
        (type === 'bin' || type === 'inventory_number') &&
        bin &&
        inventoryNumber
      ) {
        debouncedProductDetail({
          binNumber: bin,
          inventoryNumber,
          skuCode: pickList.sku_code,
          _key: key,
          pickIndex: idx,
          _state: state,
        });
      }
      if (type === 'basket_verification' && basketVerification) {
        debouncedPickItemVerification(
          {
            binNumber: bin,
            basket,
            expiryDate: new Date(expiryDate),
            inventoryNumber,
            poNumber: state.poNumber,
            quantity,
            skuCode: pickList.sku_code,
            basketVerify: basketVerification,
          },
          { _key: key, pickIndex: idx, _state: state },
        );
      } else if (bin && inventoryNumber && basket && quantity) {
        debouncedPickItem({
          binNumber: bin,
          basket,
          expiryDate: new Date(expiryDate),
          inventoryNumber,
          poNumber: state.poNumber,
          quantity,
          skuCode: pickList.sku_code,
        });
      } else if (quantity === 0 && inventoryNumber !== '' && bin !== '') {
        replaceBatch(idx, clonedPicked, clonedPicked[idx]);
      }

      if (
        nextField &&
        (value.endsWith('\t') ||
          value.endsWith('\n') ||
          (!prevValue && value.length > 1))
      ) {
        debouncedFocusNextField(
          `input[id="field-pick-${nextField}-${basket}-${idx}"]`,
        );
      }
    };

    const clearField = (field: keyof PickingInfo) => {
      let clonedPicked = structuredClone(state.picked);
      if (field === 'bin' || field === 'inventory_number') {
        const newBatch: PickingInfo = {
          bin: '',
          inventory_number: '',
          expiry_date: '',
          quantity: clonedPicked[idx].quantity,
          basket: clonedPicked[idx].basket,
          is_verified: false,
          basket_verification: 0,
        };
        clonedPicked = replaceBatch(idx, clonedPicked, newBatch);
      } else if (field === 'is_verified') {
        clonedPicked[idx][field] = false;
      } else if (field === 'quantity') {
        clonedPicked[idx][field] = 0;
      }

      const newPickProcessState = structuredClone(pickProcessState);
      newPickProcessState.set(key, { ...state, picked: clonedPicked });
      setPickProcessState(newPickProcessState);
    };

    return (
      <Grid container style={{ padding: '8px 0' }}>
        <Grid item xs={1} />
        <Grid item xs={11}>
          <Grid container>
            <Grid item xs={5}>
              <p>Bin</p>
            </Grid>
            <S.PickProcessInputGrid item xs={6}>
              <TextField
                variant="outlined"
                placeholder="-"
                value={info.bin}
                // disabled={isStockOut || !barcodeVerified}
                disabled={isStockOut}
                onChange={(event) =>
                  onFieldChange('bin', event.target.value, 'batch')
                }
                id={`field-pick-bin-${info.basket}-${idx}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => clearField('bin')}>
                        <Clear style={{ color: colors.PRIMARY_GRAY }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                }}
              />
            </S.PickProcessInputGrid>
            <Grid item xs={5}>
              <p>Batch Number</p>
            </Grid>
            <S.PickProcessInputGrid item xs={6}>
              <TextField
                variant="outlined"
                placeholder="-"
                value={info.inventory_number}
                // disabled={isStockOut || !barcodeVerified}
                disabled={isStockOut}
                onChange={(event) =>
                  onFieldChange(
                    'inventory_number',
                    event.target.value,
                    'quantity',
                  )
                }
                id={`field-pick-batch-${info.basket}-${idx}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField('inventory_number')}
                      >
                        <Clear style={{ color: colors.PRIMARY_GRAY }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                }}
              />
            </S.PickProcessInputGrid>
            <Grid item xs={5}>
              <p>Expiry Date</p>
            </Grid>
            <S.PickProcessInputGrid item xs={6}>
              <DateField
                variant="outlined"
                format="dd/MM/yyyy"
                defaultValue={null}
                value={
                  info.expiry_date ? DateTime.fromISO(info.expiry_date) : null
                }
                disabled
                onChange={(value) =>
                  onFieldChange(
                    'expiry_date',
                    value?.toFormat('dd/LL/yyyy') || '',
                  )
                }
                id={`field-pick-expiry-${info.basket}-${idx}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled>
                        <Clear style={{ color: '#fff' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                }}
              />
            </S.PickProcessInputGrid>
            <Grid item xs={5}>
              <p>Quantity Picked</p>
            </Grid>
            <S.PickProcessInputGrid item xs={6}>
              <TextField
                variant="outlined"
                placeholder="0"
                // disabled={isStockOut || !barcodeVerified}
                disabled={isStockOut}
                type="number"
                value={info.quantity || ''}
                onChange={(event) =>
                  onFieldChange('quantity', event.target.value)
                }
                id={`field-pick-quantity-${info.basket}-${idx}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => onFieldChange('quantity', '0')}
                      >
                        <Clear style={{ color: colors.PRIMARY_GRAY }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                }}
              />
            </S.PickProcessInputGrid>
            <Grid item xs={5}>
              <p>Basket Verification</p>
            </Grid>
            <S.PickProcessInputGrid item xs={6}>
              <TextField
                variant="outlined"
                placeholder="0"
                type="number"
                // disabled={isStockOut || !barcodeVerified}
                disabled={isStockOut}
                value={info.basket_verification || ''}
                onChange={(event) => {
                  onFieldChange('basket_verification', event.target.value);
                }}
                id={`field-pick-basket-verification-${info.basket}-${idx}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          onFieldChange('basket_verification', '0');
                        }}
                      >
                        {info.is_verified ? (
                          <Check style={{ color: colors.PRIMARY_GRAY }} />
                        ) : info.quantity === 0 ? (
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        ) : (
                          <ErrorOutline style={{ color: colors.PRIMARY_RED }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                }}
              />
            </S.PickProcessInputGrid>
          </Grid>
        </Grid>
      </Grid>
    );
  });
};

export default PickingProcessForm;
